import { useEffect, useState, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  UserGroupIcon,
  TicketIcon,
  PlusIcon,
  ChevronRightIcon,
  CalendarIcon,
  ClockIcon,
  FlagIcon,
  CheckCircleIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ArrowDownTrayIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, Avatar, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter, Pagination } from '@/components/ui'
import { taskforceApi } from '@/api'
import type { TaskForce, TaskForceMember, Ticket } from '@/api/taskforce'
import KanbanBoard from '@/components/taskforce/KanbanBoard'
import TaskForceChat from '@/components/taskforce/TaskForceChat'
import toast from 'react-hot-toast'

type TaskForceWithDetails = TaskForce & {
  members: TaskForceMember[]
  recent_tickets: Ticket[]
}

type ViewMode = 'list' | 'kanban'
type ColumnId = 'open' | 'in_progress' | 'review' | 'done'
type TabType = 'tickets' | 'chat'

interface KanbanData {
  columns: Record<ColumnId, { title: string; tickets: Ticket[] }>
}

export default function TaskForceDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [taskforce, setTaskforce] = useState<TaskForceWithDetails | null>(null)
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [ticketsPage, setTicketsPage] = useState(1)
  const [ticketsTotalPages, setTicketsTotalPages] = useState(1)
  const [ticketsTotal, setTicketsTotal] = useState(0)
  const [joining, setJoining] = useState(false)
  const [showNewTicketModal, setShowNewTicketModal] = useState(false)
  const [newTicket, setNewTicket] = useState({ title: '', description: '', priority: 'medium' as Ticket['priority'], status: 'open' as 'open' | 'in_progress' | 'review' | 'completed' })
  const [creating, setCreating] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('kanban')
  const [kanbanData, setKanbanData] = useState<KanbanData | null>(null)
  const [kanbanLoading, setKanbanLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('tickets')

  // Ticket Detail Modal
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [editingTicket, setEditingTicket] = useState(false)
  const [ticketForm, setTicketForm] = useState({ title: '', description: '', priority: 'medium' as Ticket['priority'], status: 'open' as Ticket['status'] })

  useEffect(() => {
    if (slug) fetchTaskForce()
  }, [slug])

  useEffect(() => {
    if (taskforce) {
      if (viewMode === 'list') {
        fetchTickets()
      } else {
        fetchKanbanData()
      }
    }
  }, [taskforce, ticketsPage, statusFilter, viewMode])

  const fetchTaskForce = async () => {
    setLoading(true)
    try {
      const data = await taskforceApi.getTaskForce(slug!)
      setTaskforce(data)
    } catch (error) {
      console.error('Error fetching task force:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTickets = async () => {
    if (!taskforce) return
    try {
      const data = await taskforceApi.getTickets(taskforce.id, {
        page: ticketsPage,
        status: statusFilter || undefined,
      })
      setTickets(data.results)
      setTicketsTotalPages(Math.ceil(data.count / 20))
      setTicketsTotal(data.count)
    } catch (error) {
      console.error('Error fetching tickets:', error)
    }
  }

  const fetchKanbanData = async () => {
    if (!slug) return
    setKanbanLoading(true)
    try {
      const data = await taskforceApi.getKanbanData(slug)
      setKanbanData(data)
    } catch (error) {
      console.error('Error fetching kanban data:', error)
    } finally {
      setKanbanLoading(false)
    }
  }

  const handleReorder = useCallback(async (ticketId: number, newStatus: ColumnId, position: number) => {
    if (!slug) return
    try {
      await taskforceApi.reorderTicket({
        ticket_id: ticketId,
        new_status: newStatus,
        position,
        taskforce_slug: slug,
      })
      // Refresh data after successful reorder
      fetchKanbanData()
    } catch (error) {
      console.error('Error reordering ticket:', error)
      toast.error('Errore nello spostamento del ticket')
      throw error
    }
  }, [slug])

  const handleExportPdf = async () => {
    if (!slug) return
    setExporting(true)
    try {
      const blob = await taskforceApi.exportPdf(slug)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `taskforce_${slug}_${new Date().toISOString().split('T')[0]}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('PDF scaricato con successo')
    } catch (error) {
      console.error('Error exporting PDF:', error)
      toast.error('Errore nell\'esportazione PDF')
    } finally {
      setExporting(false)
    }
  }

  const handleJoin = async () => {
    if (!taskforce || !slug) return
    setJoining(true)
    try {
      await taskforceApi.joinTaskForce(slug)
      toast.success('Ti sei unito alla Task Force!')
      fetchTaskForce()
    } catch (error: any) {
      console.error('Error joining task force:', error)
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail)
      } else {
        toast.error('Errore durante l\'iscrizione')
      }
    } finally {
      setJoining(false)
    }
  }

  const handleLeave = async () => {
    if (!taskforce || !slug) return
    try {
      await taskforceApi.leaveTaskForce(slug)
      toast.success('Hai lasciato la task force')
      fetchTaskForce()
    } catch (error: any) {
      console.error('Error leaving task force:', error)
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail)
      } else {
        toast.error('Errore durante l\'uscita dalla task force')
      }
    }
  }

  const handleCreateTicket = async () => {
    if (!taskforce || !newTicket.title) return
    setCreating(true)
    try {
      await taskforceApi.createTicket(taskforce.id, newTicket)
      setShowNewTicketModal(false)
      setNewTicket({ title: '', description: '', priority: 'medium', status: 'open' })
      if (viewMode === 'kanban') {
        fetchKanbanData()
      } else {
        fetchTickets()
      }
      toast.success('Ticket creato con successo')
    } catch (error) {
      console.error('Error creating ticket:', error)
      toast.error('Errore nella creazione del ticket')
    } finally {
      setCreating(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const getStatusBadge = (status: Ticket['status']) => {
    const config: Record<string, { variant: 'default' | 'info' | 'warning' | 'success' | 'danger'; label: string }> = {
      open: { variant: 'info', label: 'Aperto' },
      in_progress: { variant: 'warning', label: 'In corso' },
      review: { variant: 'info', label: 'In revisione' },
      done: { variant: 'success', label: 'Completato' },
      completed: { variant: 'success', label: 'Completato' },
      closed: { variant: 'default', label: 'Chiuso' },
    }
    return config[status] || { variant: 'default', label: status }
  }

  const getPriorityBadge = (priority: Ticket['priority']) => {
    const config: Record<Ticket['priority'], { variant: 'default' | 'info' | 'warning' | 'danger'; label: string }> = {
      low: { variant: 'default', label: 'Bassa' },
      medium: { variant: 'info', label: 'Media' },
      high: { variant: 'warning', label: 'Alta' },
      urgent: { variant: 'danger', label: 'Urgente' },
    }
    return config[priority]
  }

  const handleOpenTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket)
    setTicketForm({
      title: ticket.title,
      description: ticket.description || '',
      priority: ticket.priority,
      status: ticket.status,
    })
    setEditingTicket(false)
    setShowTicketModal(true)
  }

  const handleSaveTicket = async () => {
    if (!selectedTicket || !taskforce) return
    try {
      await taskforceApi.updateTicket(taskforce.id, selectedTicket.id, ticketForm)
      toast.success('Ticket aggiornato')
      setShowTicketModal(false)
      setSelectedTicket(null)
      if (viewMode === 'kanban') {
        fetchKanbanData()
      } else {
        fetchTickets()
      }
    } catch (error) {
      console.error('Error updating ticket:', error)
      toast.error('Errore nell\'aggiornamento del ticket')
    }
  }

  const handleDeleteTicket = async () => {
    if (!selectedTicket || !taskforce) return
    if (!confirm(`Eliminare il ticket "${selectedTicket.title}"?`)) return
    try {
      await taskforceApi.deleteTicket(taskforce.id, selectedTicket.id)
      toast.success('Ticket eliminato')
      setShowTicketModal(false)
      setSelectedTicket(null)
      if (viewMode === 'kanban') {
        fetchKanbanData()
      } else {
        fetchTickets()
      }
    } catch (error) {
      console.error('Error deleting ticket:', error)
      toast.error('Errore nell\'eliminazione del ticket')
    }
  }

  if (loading) {
    return <InlineSpinner message="Caricamento task force..." />
  }

  if (!taskforce) {
    return (
      <EmptyState
        icon={<UserGroupIcon className="h-12 w-12" />}
        title="Task force non trovata"
        description="La task force richiesta non esiste"
        action={{ label: 'Torna alle Task Force', onClick: () => navigate('/taskforce') }}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/taskforce" className="hover:text-primary-600">Task Force</Link>
        <ChevronRightIcon className="h-4 w-4" />
        <span className="text-gray-900">{taskforce.name}</span>
      </nav>

      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
            <UserGroupIcon className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{taskforce.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={taskforce.is_active ? 'success' : 'default'}>
                {taskforce.is_active ? 'Attiva' : 'Completata'}
              </Badge>
              {taskforce.is_member && (
                <Badge variant="info">
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  Membro
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Export PDF Button */}
        {taskforce.is_member && (
          <Button
            variant="outline"
            onClick={handleExportPdf}
            disabled={exporting}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {exporting ? 'Esportazione...' : 'Esporta PDF'}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content - Wider for Kanban */}
        <div className="lg:col-span-3 space-y-6">
          {/* Description Card */}
          <Card>
            <p className="text-gray-600 mb-4">{taskforce.description}</p>
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
              <Avatar size="md" name={taskforce.leader.full_name} src={taskforce.leader.avatar || undefined} />
              <div>
                <p className="text-sm text-gray-500">Leader</p>
                <p className="font-medium text-gray-900">{taskforce.leader.full_name}</p>
              </div>
            </div>
          </Card>

          {/* Tab Navigation */}
          <div className="flex items-center gap-4 border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('tickets')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === 'tickets'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <TicketIcon className="h-5 w-5" />
              <span className="font-medium">Ticket</span>
              <Badge variant="default" size="sm">
                {ticketsTotal || (kanbanData ? Object.values(kanbanData.columns).reduce((acc, col) => acc + col.tickets.length, 0) : 0)}
              </Badge>
            </button>
            <button
              onClick={() => setActiveTab('chat')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === 'chat'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5" />
              <span className="font-medium">Chat</span>
            </button>
          </div>

          {/* Chat Section */}
          {activeTab === 'chat' && (
            <TaskForceChat
              taskforceSlug={slug!}
              isMember={taskforce.is_member || false}
            />
          )}

          {/* Tickets Section */}
          {activeTab === 'tickets' && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <CardHeader title="Ticket" subtitle={`${ticketsTotal || kanbanData ? Object.values(kanbanData?.columns || {}).reduce((acc, col) => acc + col.tickets.length, 0) : 0} ticket totali`} />

              <div className="flex items-center gap-3">
                {/* View Mode Toggle */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('kanban')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'kanban'
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Vista Kanban"
                  >
                    <Squares2X2Icon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'list'
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Vista Lista"
                  >
                    <ListBulletIcon className="h-5 w-5" />
                  </button>
                </div>

                {taskforce.is_member && (
                  <Button size="sm" onClick={() => setShowNewTicketModal(true)}>
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Nuovo
                  </Button>
                )}
              </div>
            </div>

            {/* Kanban View */}
            {viewMode === 'kanban' && (
              <>
                {kanbanLoading ? (
                  <InlineSpinner message="Caricamento Kanban..." />
                ) : kanbanData ? (
                  <KanbanBoard
                    data={kanbanData}
                    onReorder={handleReorder}
                    onTicketClick={handleOpenTicket}
                  />
                ) : (
                  <EmptyState
                    icon={<Squares2X2Icon className="h-12 w-12" />}
                    title="Kanban non disponibile"
                    description="Impossibile caricare la board Kanban"
                  />
                )}
              </>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <>
                {/* Status Filter */}
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                  {[
                    { value: '', label: 'Tutti' },
                    { value: 'open', label: 'Aperti' },
                    { value: 'in_progress', label: 'In corso' },
                    { value: 'review', label: 'In revisione' },
                    { value: 'done', label: 'Completati' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setStatusFilter(opt.value); setTicketsPage(1); }}
                      className={`px-3 py-1 rounded-full text-sm whitespace-nowrap transition-colors ${
                        statusFilter === opt.value
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {tickets.length > 0 ? (
                  <>
                    <div className="divide-y divide-gray-100">
                      {tickets.map((ticket) => {
                        const statusConfig = getStatusBadge(ticket.status)
                        const priorityConfig = getPriorityBadge(ticket.priority)

                        return (
                          <div
                            key={ticket.id}
                            onClick={() => handleOpenTicket(ticket)}
                            className="flex items-start gap-4 py-4 hover:bg-gray-50 -mx-4 px-4 transition-colors cursor-pointer"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-gray-400">#{ticket.id}</span>
                                <h4 className="font-medium text-gray-900 truncate">{ticket.title}</h4>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant={statusConfig.variant} size="sm">{statusConfig.label}</Badge>
                                <Badge variant={priorityConfig.variant} size="sm">
                                  <FlagIcon className="h-3 w-3 mr-1" />
                                  {priorityConfig.label}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <span>{ticket.created_by.full_name}</span>
                                <span className="flex items-center gap-1">
                                  <CalendarIcon className="h-3 w-3" />
                                  {formatDate(ticket.created_at)}
                                </span>
                                {ticket.due_date && (
                                  <span className="flex items-center gap-1 text-orange-500">
                                    <ClockIcon className="h-3 w-3" />
                                    Scadenza: {formatDate(ticket.due_date)}
                                  </span>
                                )}
                              </div>
                            </div>
                            {ticket.assigned_to && (
                              <Avatar size="sm" name={ticket.assigned_to.full_name} src={ticket.assigned_to.avatar || undefined} />
                            )}
                          </div>
                        )
                      })}
                    </div>
                    <Pagination
                      currentPage={ticketsPage}
                      totalPages={ticketsTotalPages}
                      totalItems={ticketsTotal}
                      onPageChange={setTicketsPage}
                    />
                  </>
                ) : (
                  <EmptyState
                    icon={<TicketIcon className="h-12 w-12" />}
                    title="Nessun ticket"
                    description={taskforce.is_member ? "Crea il primo ticket per questa task force" : "Non ci sono ticket per questa task force"}
                  />
                )}
              </>
            )}
          </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions Card */}
          <Card>
            {taskforce.is_member ? (
              <div className="space-y-4">
                <p className="text-center text-green-600 font-medium">
                  <CheckCircleIcon className="h-5 w-5 inline mr-1" />
                  {taskforce.user_role === 'leader' ? 'Sei il leader' : 'Sei membro'}
                </p>
                {taskforce.user_role !== 'leader' && (
                  <button onClick={handleLeave} className="w-full text-sm text-gray-500 hover:text-red-600 transition-colors">
                    Abbandona Task Force
                  </button>
                )}
              </div>
            ) : taskforce.is_active ? (
              <Button className="w-full" onClick={handleJoin} disabled={joining}>
                {joining ? 'Iscrizione in corso...' : 'Unisciti alla Task Force'}
              </Button>
            ) : (
              <p className="text-center text-gray-500">Task force completata</p>
            )}
          </Card>

          {/* Stats Card */}
          <Card>
            <CardHeader title="Statistiche" />
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Membri</dt>
                <dd className="font-medium text-gray-900">{taskforce.members_count}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Ticket</dt>
                <dd className="font-medium text-gray-900">{taskforce.tickets_count}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Creata il</dt>
                <dd className="font-medium text-gray-900">{formatDate(taskforce.created_at)}</dd>
              </div>
              {taskforce.end_date && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Scadenza</dt>
                  <dd className="font-medium text-gray-900">{formatDate(taskforce.end_date)}</dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Members Card */}
          <Card>
            <CardHeader title="Membri" subtitle={`${taskforce.members.length} membri`} />
            <div className="space-y-3">
              {taskforce.members.slice(0, 10).map((member) => (
                <div key={member.id} className="flex items-center gap-3">
                  <Avatar size="sm" name={member.user.full_name} src={member.user.avatar || undefined} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{member.user.full_name}</p>
                    <p className="text-xs text-gray-500 capitalize">{member.taskforce_role}</p>
                  </div>
                </div>
              ))}
              {taskforce.members.length > 10 && (
                <p className="text-sm text-gray-500 text-center">
                  +{taskforce.members.length - 10} altri membri
                </p>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* New Ticket Modal */}
      <Modal isOpen={showNewTicketModal} onClose={() => setShowNewTicketModal(false)} title="Nuovo Ticket">
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
              <input
                type="text"
                value={newTicket.title}
                onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priorità</label>
              <select
                value={newTicket.priority}
                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value as Ticket['priority'] })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="low">Bassa</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stato iniziale</label>
              <select
                value={newTicket.status}
                onChange={(e) => setNewTicket({ ...newTicket, status: e.target.value as 'open' | 'in_progress' | 'review' | 'completed' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="open">Backlog</option>
                <option value="in_progress">In Progress</option>
                <option value="review">In Review</option>
                <option value="completed">Completato</option>
              </select>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowNewTicketModal(false)}>Annulla</Button>
          <Button onClick={handleCreateTicket} disabled={!newTicket.title || creating}>
            {creating ? 'Creazione...' : 'Crea Ticket'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Ticket Detail Modal */}
      <Modal
        isOpen={showTicketModal}
        onClose={() => { setShowTicketModal(false); setSelectedTicket(null); setEditingTicket(false); }}
        title={editingTicket ? 'Modifica Ticket' : `Ticket #${selectedTicket?.id}`}
      >
        <ModalBody>
          {selectedTicket && (
            <div className="space-y-4">
              {editingTicket ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
                    <input
                      type="text"
                      value={ticketForm.title}
                      onChange={(e) => setTicketForm({ ...ticketForm, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                    <textarea
                      value={ticketForm.description}
                      onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
                      <select
                        value={ticketForm.status}
                        onChange={(e) => setTicketForm({ ...ticketForm, status: e.target.value as Ticket['status'] })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="open">Aperto</option>
                        <option value="in_progress">In Corso</option>
                        <option value="review">In Revisione</option>
                        <option value="done">Completato</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priorità</label>
                      <select
                        value={ticketForm.priority}
                        onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value as Ticket['priority'] })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="low">Bassa</option>
                        <option value="medium">Media</option>
                        <option value="high">Alta</option>
                        <option value="urgent">Urgente</option>
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedTicket.title}</h3>
                  <div className="flex items-center gap-2">
                    <Badge variant={getStatusBadge(selectedTicket.status).variant}>
                      {getStatusBadge(selectedTicket.status).label}
                    </Badge>
                    <Badge variant={getPriorityBadge(selectedTicket.priority).variant}>
                      <FlagIcon className="h-3 w-3 mr-1" />
                      {getPriorityBadge(selectedTicket.priority).label}
                    </Badge>
                  </div>
                  {selectedTicket.description && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Descrizione</h4>
                      <p className="text-gray-600 whitespace-pre-wrap">{selectedTicket.description}</p>
                    </div>
                  )}
                  <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-500 space-y-1">
                    <p>Creato da: {selectedTicket.created_by.full_name}</p>
                    <p>Data: {formatDate(selectedTicket.created_at)}</p>
                    {selectedTicket.assigned_to && (
                      <p>Assegnato a: {selectedTicket.assigned_to.full_name}</p>
                    )}
                    {selectedTicket.due_date && (
                      <p className="text-orange-500">Scadenza: {formatDate(selectedTicket.due_date)}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {editingTicket ? (
            <>
              <Button variant="ghost" onClick={() => setEditingTicket(false)}>Annulla</Button>
              <Button onClick={handleSaveTicket} disabled={!ticketForm.title}>
                Salva Modifiche
              </Button>
            </>
          ) : (
            <>
              <Button variant="danger" onClick={handleDeleteTicket}>Elimina</Button>
              <Button variant="ghost" onClick={() => { setShowTicketModal(false); setSelectedTicket(null); }}>Chiudi</Button>
              {taskforce.is_member && (
                <Button onClick={() => setEditingTicket(true)}>Modifica</Button>
              )}
            </>
          )}
        </ModalFooter>
      </Modal>
    </div>
  )
}
